// Types shared between client + worker.

import type { Section } from './sections';
import type { TailoredLens } from './roleLens';

export type CallSheetStatus = 'draft' | 'published' | 'archived';

export interface EditorRecapEditor { inviteId: string; name: string; saves: number; lastEditAt: number }
export interface EditorRecapPublish { docVersion: number; at: number; by: string }
export interface EditorRecapState {
  baseVersionId: string;
  baseDocVersion: number;
  openedAt: number;
  lastEditAt: number;
  editorSaves: number;
  editors: EditorRecapEditor[];
  adminSavesDuring: number;
  publishedDuring: EditorRecapPublish[];
}
/** Light summary carried on list rows and the sheet payload. */
export interface EditorRecapSummary { names: string[]; lastEditAt: number }

export interface CallSheetSummary {
  editorRecap?: EditorRecapSummary;
  clientAnswers?: { count: number; latestAt: number };
  id: string;
  slug: string;
  title: string;
  status: CallSheetStatus;
  clientName: string | null;
  shootDateStart: string | null;
  shootDateEnd: string | null;
  updatedAt: number;
  createdAt: number;
  publishedAt: number | null;
  /** Set when archived (manual one-click from dashboard). archived_at +
   *  status='archived' move together. */
  archivedAt: number | null;
  /** R4 — set when the shoot is cancelled/rescheduled. Orthogonal to
   *  status (a cancelled sheet keeps its draft/published status so
   *  reinstating restores it exactly). While set, the public link renders
   *  a branded "this shoot has moved" page with the producer's contact. */
  cancelledAt: number | null;
  /** Set when soft-deleted via the Trash button. Cron purges entries
   *  older than 30 days. Null = not in trash. */
  trashedAt: number | null;
  /** Producer-marked "sent to crew" timestamp. Pairs with publishedAt and
   *  the acknowledgement count to give a publish → sent → ack pipeline
   *  view on the dashboard. Null = not yet marked sent. */
  sentAt: number | null;
  /** Sourced from the in-doc docType — shown on the dashboard so the
   *  producer can spot client-schedule docs at a glance. Defaults to
   *  'callsheet' for legacy/missing values. */
  docType: CallSheetDocType;
  /** Lowercased haystack for the dashboard search box — title + client +
   *  in-doc client contact + crew names. Built server-side so the client
   *  doesn't need to fetch full docs to filter. */
  searchBlob: string;
  /** Roadmap Phase 2.5 — workflow phase derivation inputs. */
  wrapSubmittedAt: number | null;
  prepWindowDays: number | null;
  /** Aggregated counts across the unified `checklist_items` table per
   *  phase. Lets the dashboard surface "X of Y prep done" / "X of Y
   *  follow-ups done" without fetching items per row. */
  prepDone: number;
  prepTotal: number;
  wrapFollowupDone: number;
  wrapFollowupTotal: number;
  /** Roadmap Phase 2.1 — dashboard pinning. When set, the row sits in
   *  a "Pinned" group at the top of the dashboard (regardless of
   *  phase). Newest pinned-at floats to the top of that group. */
  pinnedAt: number | null;
}

// ---------- Document body (CallSheetDoc) ----------
//
// Single source of truth for the editable contents of one call sheet. Stored
// as JSON in `callsheets.doc_json`. Renders to the public web view, the PDF,
// and the editor forms.
//
// `schemaVersion` lives inside the doc JSON so we can evolve the shape
// without touching SQL. The SQL `doc_version` column is a separate concept
// — it's the optimistic-lock counter and increments on every save.
//
//   undefined / 1  →  V1 shape (pre-2026-04 build). top-level `locations[]`
//                     held the location records; schedule rows had no
//                     `location` field; no `attendees`, `wardrobe`, `props`,
//                     `audioNotes`, or `referenceLocations`.
//   2              →  V2 shape (current). Locations are referenced inline
//                     from schedule rows; rich `referenceLocations` panel
//                     auto-derives from the unique location strings;
//                     attendees split out from talent; structured fields
//                     for wardrobe / props / audio replace notes-overflow.

export const CALLSHEET_SCHEMA_VERSION = 2 as const;

/** Document type — controls which sections render and which AI prompt
 *  drafts the doc.
 *
 *   'callsheet'        → full call sheet (default). All sections rendered.
 *   'client_schedule'  → simplified client-facing schedule for solo
 *                        run-and-gun shoots. Project + Schedule + venue +
 *                        notes only; crew/talent/attendees/shots/etc. all
 *                        hidden in editor + public + PDF. */
export type CallSheetDocType = 'callsheet' | 'client_schedule';

/** Coerce arbitrary input to a legal CallSheetDocType. Unknown/missing values
 *  fall back to `fallback` (default 'callsheet') so a bad payload can never
 *  produce an invalid docType. Used by the service API's optional `doc_type`
 *  param and anywhere untrusted docType input is accepted. */
export function coerceDocType(
  value: unknown,
  fallback: CallSheetDocType = 'callsheet',
): CallSheetDocType {
  return value === 'client_schedule' || value === 'callsheet' ? value : fallback;
}

export interface CallSheetDoc {
  schemaVersion?: number; // missing means V1 (pre-migrator)
  /** Defaults to 'callsheet' when missing — pre-Phase-13 docs are all full
   *  call sheets. */
  docType?: CallSheetDocType;
  /** Full-call-sheet sections currently shown, set via Manage sections.
   *  Only meaningful when docType === 'callsheet'. undefined = all sections
   *  (docs created as full call sheets). Hiding a section removes its id
   *  here and leaves its content in the doc, so re-adding restores it. */
  enabledSections?: Section[];
  client: ClientBlock;
  project: ProjectBlock;
  producer: ProducerBlock;
  /** @deprecated V1 only — V2 uses `referenceLocations` (auto-derived from schedule) */
  locations?: LocationRow[];
  referenceLocations: ReferenceLocationRow[];
  crew: CrewRow[];
  talent: TalentRow[];
  attendees: AttendeeRow[];
  interviewQuestions: InterviewQuestionGroup[];
  schedule: ScheduleRow[];
  filmingSpecs: FilmingSpecsBlock;
  mandatoryShots: MandatoryShotRow[];
  /** Media captured on this shoot. Missing values on older docs mean video. */
  captures?: 'video' | 'photo' | 'both';
  /** Mandatory shots view preference. 'time' (default) renders the list
   *  chronologically; 'location' groups shots by their `location` field
   *  for on-the-day reference. */
  mandatoryShotsGroupBy?: 'time' | 'location';
  safety: SafetyBlock;
  /** Bordered orange callouts above safety protocols — for hard rules like
   *  "do NOT film Kylie Armstrong" that must not be missed. One sentence each. */
  complianceRules: string[];
  /** AI-raised questions where the brief was unclear or the model couldn't
   *  confidently fill a field. Surfaced in the editor so the producer can
   *  resolve them rather than discover gaps mid-shoot. */
  clarifications?: ClarificationItem[];
  dataWrangle: string;
  dressCode: string;
  catering: string;
  wardrobe: string;
  props: string;
  audioNotes: string;
  extraNotes: string;
  /** Producer-only notes — pre-shoot tasks, post-shoot logistics, production
   *  decisions. Visible in the editor only; never rendered on the public web
   *  view or the PDF. AI routes brain-dump content here. */
  internalNotes: string;
  /** Per-document branding. White label applies to shared views and PDFs. */
  branding?: BrandingBlock;
  /** Roadmap Phase 1.2 — wrap-notes prose sections. Internal-only; never
   *  rendered in the public CallSheetView. Follow-up tasks live in the
   *  `checklist_items` table with phase='wrap_followup', not here. */
  wrapNotes?: WrapNotesBlock;
  /** Client report content, exposed only through the enabled client report. */
  wrapClientNote?: string;
  /** Missing uses Settings on first edit or wrap submission; [] is intentional. */
  nextSteps?: string[];
  /** Missing stays hidden until submit-wrap defaults it on. Explicit false wins. */
  showDayReport?: boolean;
  /** Roadmap Phase 6.2 — script snapshots the producer manually added to this
   *  shoot. R6.3 flipped the model from include-list to exclude-list (see
   *  `scriptLinksDismissed`), so this field is no longer written by the editor.
   *  Kept on the type as a fallback the worker uses when the dashboard manifest
   *  is unreachable, and for back-compat with R6.2-era docs. */
  scriptLinks?: ScriptLinkRef[];
  /** Roadmap Phase 6.3 — scriptgen_script_id values the producer has hidden
   *  from this specific shoot. Default behaviour is "show every script in the
   *  manifest"; the producer × Hides ones that don't apply to this shoot day
   *  (e.g. videos shooting on a different date). Per-callsheet, so dismissing
   *  a script from Shoot A doesn't dismiss it from Shoot B. */
  scriptLinksDismissed?: string[];
  /** Job scope requires an exact manifest external_ref match. Legacy docs use client scope. */
  scriptLinksScope?: 'job' | 'client';
  /** Roadmap Phase 6.5 — last successful sync of script-sourced
   *  interview question groups (epoch ms). Drives the on-editor-open
   *  silent refresh: stale (>24h) or missing → re-sync best-effort.
   *  Updated by every successful run of the refresh endpoint or cron. */
  interviewQuestionsSyncedAt?: number;
  /** Metadata-only summaries keyed by scriptgen_script_id. Producer-only. */
  scriptMeta?: Record<string, ScriptMeta>;
  /** Last script sync check and its availability. Producer-only. */
  scriptSyncHealth?: {
    checkedAt: number;
    state: 'ok' | 'degraded' | 'unavailable';
    failedScriptIds?: string[];
  };
  /** R7 / WP1: explicit per-sheet choice for the role-based identity gate
   *  ("Tailored views"). undefined = the default, resolved by
   *  `roleViewsEnabled` (shared/roleLens.ts): ON only for docs created as
   *  full call sheets (`docType==='callsheet'` with no `enabledSections`),
   *  OFF for anything that started as a schedule, escalated or not. The
   *  legacy `roleViewsDisabled: true` is migrated to `'off'` by docMigrator.
   *  PUBLIC: the public client needs it to decide whether to gate.
   *  Presentation-layer only, not access control. */
  roleViews?: 'on' | 'off';
  /** Producer opt-in. Signing also requires release wording in Settings. */
  onCameraRelease?: boolean;
  /** P5: per-lens section picks for Tailored views. Absent key = that lens's
   *  defaults (shared/roleLens.ts LENS_SECTION_DEFAULTS). Include/exclude
   *  only, no ordering. PUBLIC: the public client renders lenses from it.
   *  Presentation-layer only, not access control. */
  lensSections?: Partial<Record<TailoredLens, Section[]>>;
  /** PUBLIC: passed through publicProjection; absent means off. */
  agentTools?: 'on' | 'off';
  /** WP1: origin marker. Set to 'callsheet' when a doc created as a full
   *  call sheet first gets an explicit `enabledSections` list (Manage
   *  sections), so the created-as-full-sheet gate default survives the
   *  conversion while the doc stays a call sheet. Never set on docs that
   *  started as schedules. PUBLIC: an input to roleViewsEnabled. */
  createdAs?: 'callsheet';
  // ---- Client-experience wave (PLAN_V3 Phase 2). All six fields below are
  //      PUBLIC (rendered to clients/crew) — see publicProjection. Every one
  //      is listed in docMigrator + fullCallSheetDoc() (Hard rule 2). ----
  /** Personal note under the public title (approved mockup M1). Toggle is
   *  per-sheet; when absent/disabled nothing renders — no empty placeholder. */
  personalNote?: { enabled: boolean; text: string };
  /** Producer photo card (M1 — Henry's photo only, no crew/talent photos).
   *  undefined = default by docType (ON for client_schedule, OFF for full
   *  call sheets); an explicit {enabled:false} sticks. */
  producerCard?: { enabled: boolean };
  /** 2C calendar identity. 'v2' (set server-side on a sheet's first publish
   *  and sticky after) uses the stable sheet, person and day UID; absent keeps
   *  the pre-2C title and date UID so events crew already imported still update. */
  calendarUidScheme?: 'v2';
  /** Forecast and golden-hour card. Per-sheet opt-in, OFF when absent. */
  weatherCard?: { enabled: boolean };
  /** Client FAQ rows (M1/M7) — rendered as collapsed rows at the bottom of
   *  client/full views; hidden from crew/talent lenses. */
  faq?: FaqItem[];
  /** On-camera prep guide (M7): static copy module, per-sheet toggle.
   *  undefined = default ON when the doc has talent rows. Talent lens only. */
  prepGuide?: { enabled: boolean };
  /** Exterior-shoot flag (M7) — drives the Phase 4 weather alerts. */
  exteriorShoot?: boolean;
}

/** One client-FAQ row (client-experience wave). `id` is a ulid for React
 *  keys + drag-reordering. */
export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

/** Roadmap Phase 4 — per-callsheet automation toggles (approved mockup M4).
 *  Stored as JSON in the `callsheets.automation_prefs` COLUMN — this is
 *  deliberately NOT a `CallSheetDoc` field, so it does NOT go through
 *  docMigrator/publicProjection (see `src/worker/lib/db.ts`'s
 *  parse/serialize helpers instead). Every field is optional and defaults
 *  to false/absent — automations are opt-in per sheet, never on by default.
 *
 *  `dayTrackerEnabled` belongs conceptually to Phase 5's shoot-day tracker,
 *  but lives on this same panel/type per the approved M4 placement; its
 *  toggle exists from Phase 4, its behaviour ships in Phase 5. */
export interface AutomationPrefs {
  /** T-24h before general call: nudge crew who haven't confirmed yet. */
  confirmNudge24h?: boolean;
  /** T-4h before general call: departure summary to all recipients. */
  departureSummary4h?: boolean;
  /** Retired in 2C (no toggle, no effect): the editor's unsent changes chip
   *  and leave prompt replaced it. Kept so stored prefs still parse. */
  changeRebroadcastPrompt?: boolean;
  /** Post-wrap thank-you + review-ask email (Phase 5.2). */
  wrapFollowup?: boolean;
  /** Exterior-shoot forecast check (only meaningful when
   *  `doc.exteriorShoot` is true) — alerts Henry only, never crew. */
  weatherAlert?: boolean;
  /** Turns on the `/day/:slug` live shoot-day tracker (Phase 5.1). */
  dayTrackerEnabled?: boolean;
}

/** Metadata-only scripting-tool summary. Never contains script content. */
export interface ScriptMeta {
  family: 'brief' | 'script';
  talentName?: string | null;
  talentNameClient?: boolean;
  templateKey: string;
  slug: string;
  hasShootView: boolean;
  shootViewPublicUrl: string | null;
  shootViewAdminUrl: string | null;
  counts: { questions: number; toCameraRows: number; voiceoverRows: number; totalRows: number };
  updatedAt: string | null;
  fetchedAt: number;
}

/** Public summary with question counts derived from verified public groups. */
export interface PublicScriptMeta {
  family: 'brief' | 'script';
  /** Present only when the upstream talent field is client-facing. */
  talentName?: string;
  /** Explicit consent marker, emitted only alongside a public talent name. */
  talentNameClient?: true;
  hasShootView: boolean;
  shootViewPublicUrl: string | null;
  counts: { questions: number; toCameraRows: number; voiceoverRows: number; totalRows: number };
  updatedAt: string | null;
}

/** Snapshot of one script entry from the dashboard manifest. The id is the
 *  scripting tool's stable identifier and is the join key for refreshes. */
export interface ScriptLinkRef {
  scriptgen_script_id: string;
  title: string;
  url: string;
  meta?: PublicScriptMeta;
}

/** Three optional prose sections captured at wrap. All internal-only —
 *  never on the public view or the PDF. Empty fields stay undefined so
 *  the JSON payload doesn't bloat. */
export interface WrapNotesBlock {
  production?: string;
  footage?: string;
  issues?: string;
}

/** Per-document white label plus optional PDF appearance overrides.
 *  Missing fields retain existing studio defaults. */
export interface BrandingBlock {
  /** Hide studio branding on shared schedules, PDF exports and schedule emails. Default off. */
  whiteLabel?: boolean;
  /** Replaces "SHEAHAN STUDIOS" in the PDF wordmark. Plain text only. */
  wordmark?: string;
  /** Hex color for accent elements in the PDF (eyebrow text, hero card
   *  border, schedule highlights). e.g. "#0066CC". */
  accentColor?: string;
}

export interface ClientBlock {
  id?: string;             // links back to clients.id when picked from directory
  name: string;            // business / company name
  contactName?: string;
  contactRole?: string;    // contact person's role at the business (e.g. "Comms Manager")
  contactEmail?: string;
  contactMobile?: string;
}

export interface ProjectBlock {
  title: string;
  overview: string;        // 2–4 sentence summary
  shootDates: string[];    // ISO YYYY-MM-DD strings, multi-day shoots have >1
  weather?: string;
}

export interface ProducerBlock {
  name: string;            // hardcoded "Henry Sheahan"
  mobile: string;
  email: string;
  start?: string;          // HH:MM 24h
  end?: string;            // HH:MM 24h
  /** True when the producer is reachable remotely rather than on set. */
  remote?: boolean;
  /** Name of the crew member who leads on set when the producer is remote. */
  onSetContact?: string;
}

/** V1 location section. Kept for migrator input only — not edited in V2. */
export interface LocationRow {
  id?: string;
  label: string;
  address: string;
  parking?: string;
  parkingAccess?: string;
  contactName?: string;
  contactMobile?: string;
  inductionUrl?: string;
  nearestHospital?: string;
  notes?: string;
}

/** V2 reference panel. Auto-populated from schedule.location values, with
 *  the rich details (parking, hospital, induction) attached when picked
 *  from the locations directory. Multiple schedule rows with the same
 *  label share the same entry — de-duped by `label`. */
export interface ReferenceLocationRow {
  roomBooked?: boolean;
  buildingAccessTime?: string; // HH:MM
  id?: string;             // directory id when picked
  label: string;           // joins to schedule[].location
  address?: string;
  parking?: string;
  parkingAccess?: string;
  contactName?: string;
  contactMobile?: string;
  inductionUrl?: string;
  nearestHospital?: string;
  notes?: string;
}

export interface CrewRow {
  discipline?: 'video' | 'photo' | 'both';
  id?: string;
  name: string;
  role: string;
  mobile?: string;
  email?: string;
  start?: string;          // HH:MM
  end?: string;            // HH:MM
  equipment?: string;
}

/** On-camera people only — interview subjects, presenters, scene actors. */
export interface TalentRow {
  name: string;
  role?: string;
  activity?: string;
  callTime?: string;       // HH:MM
  wrapTime?: string;       // HH:MM
  dressCode?: string;
  notes?: string;
}

/** Off-camera people who need to appear on the sheet but aren't crew or
 *  talent — clients, PR/comms, security, drivers, hosts, bus passengers. */
export interface AttendeeRow {
  name: string;
  role?: string;            // e.g. "Client", "PR", "Security lead"
  organisation?: string;    // company / department
  mobile?: string;
  email?: string;
  notes?: string;
}

export interface InterviewQuestionGroup {
  /** Synced cue points aligned with questions; omitted when all are empty. */
  hints?: string[][];
  /** Synced audience verification. Producer-only unless false on a synced
   *  group. Never on public payloads. */
  internal?: boolean;
  audience: string;        // e.g. "Exec / founder", "Vox pop", "Political"
  questions: string[];
  /** Roadmap Phase 4.3 — when true, the group renders in a "Shared
   *  questions" bucket at the top of the section, separate from the
   *  per-talent groups. The `audience` field is ignored for shared
   *  groups. Optional / default false for back-compat. */
  shared?: boolean;
  /** R6.5 — when set, this group was synced from the matching script
   *  (`scriptgen_script_id`) via the daily cron / manual refresh /
   *  on-editor-open silent sync. Editor surfaces a "Synced from {script}"
   *  badge so the producer knows edits will be overwritten on the next
   *  sync. Manually-authored groups (no `sourceScriptId`) are never
   *  touched by sync. */
  sourceScriptId?: string;
}

export interface ScheduleRow {
  /** Fixed rows keep their time during schedule shifts. */
  fixed?: boolean;
  time: string;            // HH:MM
  activity: string;
  location?: string;       // V2 — joins to referenceLocations[].label
  notes?: string;
  day?: number;            // 1-indexed, only for multi-day shoots
  /** Visual highlight for key moments — bump-in, lunch, wrap, location
   *  changes. Renders as a coloured left border on web + PDF. */
  highlight?: ScheduleHighlight;
}

export type ScheduleHighlight = 'key' | 'meal' | 'move';

export interface FilmingSpecsBlock {
  colourProfile?: string;  // C-Log, S-Log3, S-Cinetone…
  frameRates?: string;     // "25p interviews / 50p B-roll / 100p slow-mo"
  resolution?: string;     // "4K"
  codec?: string;          // "XAVC-I"
  extras?: string;         // 2-camera, teleprompter, drone, etc.
}

export interface MandatoryShotRow {
  id?: string;
  time?: string;           // HH:MM (optional — many shots are not time-anchored)
  description: string;
  location?: string;       // V2 — joins to referenceLocations[].label, used to group shots
  category?: ShotCategory; // V2 — phase 5 will use this for grouping; phase 1 leaves nullable
  starred?: boolean;
  /** Missing values on older rows mean video. */
  media?: 'video' | 'photo';
}

export type ShotCategory = 'hero' | 'broll' | 'interview' | 'cutaway' | 'group' | 'social';

export interface SafetyBlock {
  emergency: string;       // default: "In an emergency, dial 000."
  hospitalAddress: string; // default: "TBC — confirm before shoot day"
  protocols?: string;      // PPE, induction notes, patient privacy, etc.
}

// ---------- Directory rows (DTOs sent to client) ----------

export interface ClientDirectoryEntry {
  id: string;
  /** Business / company name. */
  name: string;
  /** Contact person at that business. */
  contactName: string | null;
  /** Contact person's role inside the business (e.g. "Comms Manager"). */
  contactRole: string | null;
  contactEmail: string | null;
  contactMobile: string | null;
  notes: string | null;
}

export interface CrewDirectoryEntry {
  id: string;
  name: string;
  roleDefault: string | null;
  mobile: string | null;
  email: string | null;
  equipmentDefault: string | null;
  notes: string | null;
}

/** D2 — a crew contact fetched from the OS's crew register (read-only
 *  convenience insert, no persisted linkage — see
 *  src/worker/lib/crewDirectory.ts and CallSheetView.tsx's pickOsCrew).
 *  Mirrors `PublicCrewContact` in the OS's crewService.ts on the wire. */
export interface OsCrewContact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
}

// ---------- AI draft I/O ----------

export interface DraftRequest {
  source: 'transcript' | 'brief';
  text: string;
  /** Defaults to 'callsheet'. Switches the AI prompt + tool to produce a
   *  simplified solo-shoot doc instead of a full call sheet. */
  docType?: CallSheetDocType;
}

export type DraftResponse =
  | { ok: true; doc: CallSheetDoc; shootType: ShootType }
  | { ok: false; error: string };

export type ShootType = 'event' | 'facility' | 'interview' | 'bts';

// ---------- AI clarifications ----------

export interface ClarificationItem {
  /** Stable id (ulid) for React keys + tracking. */
  id: string;
  /** What the AI is asking — one sentence. */
  question: string;
  /** Optional dot-path hint (e.g., "safety.hospitalAddress") so the editor
   *  can deep-link or annotate the relevant field. */
  fieldHint?: string;
  /** AI's best-guess answer if available — user can accept it as-is or override. */
  suggestedAnswer?: string;
  /** Resolution state. Pending = visible in the clarifications panel. */
  status: 'pending' | 'answered' | 'dismissed';
  /** Free-form answer the user provided. Tracked so we can re-draft later. */
  userAnswer?: string;
}

// ---------- Checklist items (Roadmap Phase 1.1 + 1.2) ----------

/** One row in the unified `checklist_items` table. Backs both the
 *  pre-shoot checklist (phase='prep') and the wrap follow-up tasks
 *  (phase='wrap_followup'). UI keeps the two phases visually separate. */
export type ChecklistPhase = 'prep' | 'wrap_followup';

export interface ChecklistItem {
  id: string;
  callsheetId: string;
  phase: ChecklistPhase;
  label: string;
  note: string | null;
  doneAt: number | null;
  dueAt: number | null;
  position: number;
  createdAt: number;
}

// ---------- Crew availability polls (Roadmap Phase 1.3) ----------

export type AvailabilityStatus = 'yes' | 'no' | 'maybe';

export interface AvailabilityPoll {
  id: string;
  brief: string;
  dates: string[]; // YYYY-MM-DD strings
  deadline: number | null;
  archivedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface AvailabilityInvite {
  id: string;
  pollId: string;
  /** Optional link to a `crew` directory row. Captured when the producer
   *  picked from the directory; null for free-text or pre-0009 invites. */
  crewId: string | null;
  /** Always populated. Captured at invite create time so an archived
   *  directory entry doesn't blank out a historical poll. */
  crewName: string;
  /** Raw share credential only in the create response; empty on later reads. */
  token: string;
  createdAt: number;
}

export interface AvailabilityResponse {
  inviteId: string;
  date: string; // YYYY-MM-DD
  status: AvailabilityStatus;
  note: string | null;
  respondedAt: number;
}

// ---------- Additional shots (Roadmap Phase 6.1) ----------
//
// Captured on shoot day from /shoot/:slug. Distinct from
// `mandatoryShots` (which lives in the doc) — additions are
// operational, not part of the published brief.

export interface AdditionalShot {
  id: string;
  callsheetId: string;
  description: string;
  /** Optional, matches ShotCategory enum when set. */
  category: ShotCategory | null;
  /** Self-reported crew name. Required at add time. */
  addedBy: string;
  addedAt: number;
  /** null = not yet ticked. Shared across viewers — whoever ticks
   *  marks it done for everyone. */
  doneAt: number | null;
}
